import React, { useState } from 'react';
import { ToggleButton, ToggleButtonGroup, Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subMonths,
} from 'date-fns';
import { useTranslation } from 'react-i18next';
import DateRangePicker from '../../components/DateRangePicker';

type Preset = 'today' | 'week' | 'month' | 'last_month' | 'custom';

interface DatePresetBarProps {
  onChange: (start: Date, end: Date) => void;
  startDate: Date | null;
  endDate: Date | null;
  onCustomChange: (start: Date | null, end: Date | null) => void;
}

const DatePresetBar: React.FC<DatePresetBarProps> = ({
  onChange,
  startDate,
  endDate,
  onCustomChange,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activePreset, setActivePreset] = useState<Preset | null>(null);

  const applyPreset = (preset: Preset) => {
    setActivePreset(preset);
    const now = new Date();
    switch (preset) {
      case 'today':
        onChange(startOfDay(now), endOfDay(now));
        break;
      case 'week':
        onChange(startOfWeek(now, { weekStartsOn: 1 }), endOfWeek(now, { weekStartsOn: 1 }));
        break;
      case 'month':
        onChange(startOfMonth(now), endOfMonth(now));
        break;
      case 'last_month': {
        const lastMonth = subMonths(now, 1);
        onChange(startOfMonth(lastMonth), endOfMonth(lastMonth));
        break;
      }
      case 'custom':
        break;
    }
  };

  const handleToggle = (_: React.MouseEvent, value: Preset | null) => {
    if (!value) return;
    applyPreset(value);
  };

  return (
    <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={1} mb={2} flexWrap="wrap">
      <ToggleButtonGroup
        value={activePreset}
        exclusive
        onChange={handleToggle}
        size="small"
        sx={{ flexWrap: 'wrap' }}
      >
        <ToggleButton value="today">{t('report.preset_today')}</ToggleButton>
        <ToggleButton value="week">{t('report.preset_this_week')}</ToggleButton>
        <ToggleButton value="month">{t('report.preset_this_month')}</ToggleButton>
        <ToggleButton value="last_month">{t('report.preset_last_month')}</ToggleButton>
        <ToggleButton value="custom">{t('report.preset_custom')}</ToggleButton>
      </ToggleButtonGroup>

      {activePreset === 'custom' && (
        <Box minWidth={300}>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={(start, end) => {
              onCustomChange(start, end);
              if (start && end) {
                onChange(start, end);
              }
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default DatePresetBar;
